import { Box, Button, Checkbox, FormControl, FormLabel, HStack, useColorModeValue, useToast } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';

import FormField from '../../../components/fields/FormField';
import PageCard from '../../../components/page-card';
import { translate } from '../../../i18n';
import { typography } from 'theme/tokens';
import { CourtDto } from '../../../models/court/dto/court.dto';
import SportSelect from 'components/SportSelect';
import LocationPicker from '../../location-management/components/LocationPicker';
import { useSingleClubAutoSelect } from '../../location-management/hooks/useSingleClubAutoSelect';
import { useIsOrgManager } from '../../auth/roles';
import { buildCapacityPricingLossWarning } from '../../../utils/capacity-pricing-warnings';
import { useUpdateCourt } from '../hooks/useUpdateCourt';

type Props = { court: CourtDto };
export default function CourtDetailsCard({ court }: Props) {
    // Court updates are admin-only on the API; hide the save affordance for
    // coaches (the form fields stay visible as a read view).
    const isOrgManager = useIsOrgManager();
    const toast = useToast();
    const labelColor = useColorModeValue('secondaryGray.900', 'white');
    const { mutateAsync: updateCourt, isLoading: isUpdating } = useUpdateCourt(court.id, (updated) => {
        // Narrowing this court's sports can take away the venue's LAST price
        // for a size it still offers, and every guard on this rail refuses only
        // when a booking already exists — so a court with nothing upcoming
        // saved silently and the product stopped selling behind a green toast
        // (D136). The warning replaces the success toast rather than stacking
        // on it, the way the game-duration rails already report the same class.
        const warning = buildCapacityPricingLossWarning(updated);
        if (warning) {
            toast({
                title: translate('courts.capacity_pricing_lost_title'),
                description: warning,
                status: 'warning',
                duration: 10000,
                isClosable: true,
                position: 'top-right'
            });
            return;
        }
        toast({
            title: translate('courts.court_edited_successfully'),
            status: 'success',
            duration: 3000,
            position: 'top-right'
        });
    }, () => {
        toast({
            title: translate('error'),
            status: 'error',
            duration: 3000,
            position: 'top-right'
        });
    });
    const [hasBeenEdited, setHasBeenEdited] = useState(false);
    const [editedCourt, setEditedCourt] = useState(() => {
        if (!court)
            return null;
        return { ...court };
    });

    useEffect(() => {
        if (court && !editedCourt)
            setEditedCourt({ ...court });
    }, [court, editedCourt]);

    const onValueChange = useCallback((key: keyof typeof editedCourt, value: any) => {
        setEditedCourt(prev => {
            return { ...prev, [key]: value };
        });
        setHasBeenEdited(true);
    }, [setEditedCourt]);

    // MYS-66: hide the club selector when the org has a single club. The court
    // already belongs to that sole club, so auto-select is a no-op (guarded).
    const { isSingleClub } = useSingleClubAutoSelect({
        selectedLocation: editedCourt?.location,
        onSelect: (loc) => onValueChange('location', loc),
    });

    return (
        <PageCard
            showBackButton
            title={`${court.name}`}
            rightHeader={
                isOrgManager ? (
                    <HStack spacing={2}>
                        <Button
                            disabled={!hasBeenEdited}
                            isLoading={isUpdating}
                            colorScheme="brand"
                            onClick={() => { updateCourt(editedCourt); }}
                        >
                            {translate("save")}
                        </Button>
                    </HStack>
                ) : null
            }
        >
            <Box px={6} pb="150px">
                <FormControl>
                    <FormLabel {...typography.formLabel} color={labelColor} mb="6px">{translate('court.sports')}</FormLabel>
                    <SportSelect value={editedCourt.sports || []} onChange={(newValue) => {
                        onValueChange('sports', newValue)
                    }} />
                </FormControl>
                <FormField label={translate('court.name')} value={editedCourt?.name} onChange={(newValue: string) => onValueChange('name', newValue)} />
                <FormField
                    type="number"
                    label={translate('courts.coaching_priority')}
                    helperText={translate('courts.coaching_priority_helper')}
                    value={editedCourt.coachingPriority ?? ''}
                    min={1}
                    step={1}
                    placeholder={translate('courts.coaching_priority_placeholder')}
                    onChange={(value: number) => onValueChange('coachingPriority', value)}
                    onEmpty={() => onValueChange('coachingPriority', null)}
                />
                <FormControl>
                    <FormLabel {...typography.formLabel} color={labelColor} mb="6px">{translate('courts.is_outdoor') || 'Outdoor court'}</FormLabel>
                    <Checkbox
                        isChecked={editedCourt.isOutdoor ?? false}
                        onChange={(e) => onValueChange('isOutdoor', e.target.checked)}
                    >
                        {translate('courts.is_outdoor_description') || 'This court is outdoors (weather will be shown)'}
                    </Checkbox>
                </FormControl>
                {!isSingleClub && (
                    <FormControl>
                        <FormLabel {...typography.formLabel} color={labelColor} mb="6px">{translate('event.club')}</FormLabel>
                        <LocationPicker selectedLocation={editedCourt.location} setSelectedLocation={e => {
                            onValueChange('location', e);
                        }} />
                    </FormControl>
                )}
            </Box>
        </PageCard>
    );
}
